import styled from '@emotion/styled'
import { Grid, Theme } from '@yuno/design-system-web'
import ConnectionSection from '../../components/connection-section'
import ConnectionCurrencies from '../../components/connection-currencies'
import { ConnectionInfo } from '../../components/connection-info'
import { useLocation, useNavigate } from 'react-router-dom'
import { ConnectionMap } from '../../components/connection-map'
import type { UseQueryResult } from 'react-query'
import { ConnectionPaymentMethod } from '../../components/connection-payment-method'
import { useGetPaymentMethodsByProvider } from '@yuno/dashboard-api-mfe'
import { useEffect, useState } from 'react'
import * as cc from 'currency-codes'
import {
  BreadcrumbPortal,
  CaretIconStyled,
  useClearFilterEvent,
} from '@yuno/dashboard-container-mfe'
import { useTranslation } from 'react-i18next'

type StyledProps = { theme?: Theme }

const Container = styled(Grid)<StyledProps>`
  background-color: ${({ theme }) => theme.palette.grey[0]};
  margin-top: -27px;
`

const VerticalSpacing = styled('div')`
  height: 0px;
  margin-top: 20px;
`

const buildSectionName = ({
  array,
  title,
  plural,
}: {
  array: Array<any>
  title: string
  plural?: string
}) => {
  const thereAreItems = array?.length > 1
  const pluralTitle = `${thereAreItems ? (plural ? plural : `${title}s`) : ''}`
  return array?.length
    ? `${array?.length} ${thereAreItems ? pluralTitle : title}`
    : ''
}

const showAllCurrenciesValue = 'ALL_CURRENCIES'

const ConnectionDetails = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { connection }: any = location?.state
  const [currencies, setCurrencies] = useState([])
  const [showAllCurrencies, setShowAllCurrencies] = useState(false)
  const { t } = useTranslation()

  useClearFilterEvent(() => {
    navigate('/connections', { replace: true })
  })

  const {
    data: payment_methods_data,
    isLoading,
  }: UseQueryResult<any, unknown> = useGetPaymentMethodsByProvider({
    provider: connection?.id || connection?.providerId,
  })

  useEffect(() => {
    const showAllCurrencies =
      connection?.currencies?.filter(
        (currency) => currency === showAllCurrenciesValue,
      ).length > 0
    setShowAllCurrencies(showAllCurrencies)
    const currencies = showAllCurrencies ? cc.codes() : connection?.currencies
    setCurrencies(currencies)
  }, [connection?.currencies])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const renderBreadcrumbs = () => {
    return (
      <>
        {[`${connection?.name} details`].map((text, key) => (
          <BreadcrumbPortal key={key}>
            <CaretIconStyled />
            {text}
          </BreadcrumbPortal>
        ))}
      </>
    )
  }

  return (
    <Container container spacing={2}>
      {renderBreadcrumbs()}
      <Grid item xl={3} md={5} xs={8}>
        <ConnectionInfo
          id={connection?.id}
          title={connection?.name}
          category={connection?.category}
          description={connection?.description}
          capabilities={connection?.capabilities}
          website={connection?.website}
          img={connection?.icon}
        />
      </Grid>
      <Grid item xl={9} md={12} xs={12}>
        <ConnectionSection
          textLeft={t('paymentMethod')}
          textRight={buildSectionName({
            array: payment_methods_data,
            title: t('method'),
          })}
          child={
            <ConnectionPaymentMethod
              paymentMethods={payment_methods_data}
              isLoading={isLoading}
            />
          }
        />
        <VerticalSpacing />
        <ConnectionSection
          textLeft={t('coverage')}
          textRight={buildSectionName({
            array: showAllCurrencies
              ? new Array(206)
              : connection?.location_coverage,
            title: t('location'),
          })}
          child={
            <ConnectionMap location_coverage={connection?.location_coverage} />
          }
        />
        <VerticalSpacing />
        <ConnectionSection
          textLeft={t('currencies')}
          textRight={buildSectionName({
            array: showAllCurrencies ? new Array(180) : currencies,
            title: t('currency'),
            plural: t('currencies'),
          })}
          child={
            <ConnectionCurrencies
              showAllCurrencies={showAllCurrencies}
              currencies={currencies}
            />
          }
        />
      </Grid>
    </Container>
  )
}

export default ConnectionDetails
